import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CalendarGrid = ({
  viewMode,
  selectedEmployees,
  currentDate,
  onTimeSlotClick,
}) => {
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  // Mock data for demonstration
  const timeSlots = [
    '۰۸:۰۰',
    '۰۸:۳۰',
    '۰۹:۰۰',
    '۰۹:۳۰',
    '۱۰:۰۰',
    '۱۰:۳۰',
    '۱۱:۰۰',
    '۱۱:۳۰',
    '۱۲:۰۰',
    '۱۲:۳۰',
    '۱۳:۰۰',
    '۱۳:۳۰',
    '۱۴:۰۰',
    '۱۴:۳۰',
    '۱۵:۰۰',
    '۱۵:۳۰',
    '۱۶:۰۰',
    '۱۶:۳۰',
    '۱۷:۰۰',
    '۱۷:۳۰',
    '۱۸:۰۰',
    '۱۸:۳۰',
    '۱۹:۰۰',
    '۱۹:۳۰',
  ];

  const persianDays = [
    'شنبه',
    'یکشنبه',
    'دوشنبه',
    'سه‌شنبه',
    'چهارشنبه',
    'پنج‌شنبه',
    'جمعه',
  ];

  const employees = [
    { id: 1, name: 'مریم احمدی', color: 'bg-blue-100 border-blue-300' },
    { id: 2, name: 'فاطمه رضایی', color: 'bg-green-100 border-green-300' },
    { id: 3, name: 'زهرا محمدی', color: 'bg-purple-100 border-purple-300' },
    { id: 5, name: 'نرگس حسینی', color: 'bg-pink-100 border-pink-300' },
  ];

  // Generate mock availability data
  const generateAvailabilityData = () => {
    const data = {};
    selectedEmployees?.forEach((empId) => {
      data[empId] = {};
      timeSlots?.forEach((time) => {
        persianDays?.forEach((day, dayIndex) => {
          const random = Math.random();
          let status = 'available';
          if (random < 0.2) status = 'busy';
          else if (random < 0.3) status = 'unavailable';

          data[empId][`${dayIndex}-${time}`] = {
            status,
            client: status === 'busy' ? 'مشتری رزرو شده' : null,
            service: status === 'busy' ? 'کوتاهی مو' : null,
          };
        });
      });
    });
    return data;
  };

  const availabilityData = generateAvailabilityData();

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-success/20 border-success/40 hover:bg-success/30';
      case 'busy':
        return 'bg-error/20 border-error/40 hover:bg-error/30';
      case 'unavailable':
        return 'bg-muted border-border hover:bg-muted/80';
      default:
        return 'bg-card border-border hover:bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return 'Check';
      case 'busy':
        return 'X';
      case 'unavailable':
        return 'Minus';
      default:
        return null;
    }
  };

  const handleTimeSlotClick = (employeeId, day, time, currentStatus) => {
    const slotKey = `${day}-${time}`;
    setSelectedTimeSlot({ employeeId, day, time, slotKey, currentStatus });
    onTimeSlotClick?.({ employeeId, day, time, currentStatus });
  };

  if (viewMode === 'day') {
    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            برنامه روزانه
          </h3>
          <p className="text-sm text-muted-foreground">۱۵ مهر ۱۴۰۴</p>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[600px]">
            {/* Header */}
            <div className="grid grid-cols-[120px_1fr] border-b border-border">
              <div className="p-3 bg-muted border-l border-border">
                <span className="text-sm font-medium text-foreground">
                  زمان
                </span>
              </div>
              <div className="grid grid-cols-4 gap-px bg-border">
                {employees
                  ?.filter((emp) => selectedEmployees?.includes(emp?.id))
                  ?.map((employee) => (
                    <div key={employee?.id} className="p-3 bg-card text-center">
                      <span className="text-sm font-medium text-foreground">
                        {employee?.name}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Time slots */}
            <div className="max-h-96 overflow-y-auto">
              {timeSlots?.map((time, timeIndex) => (
                <div
                  key={time}
                  className="grid grid-cols-[120px_1fr] border-b border-border last:border-b-0">
                  <div className="p-3 bg-muted border-l border-border flex items-center">
                    <span className="text-sm font-medium text-foreground">
                      {time}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-px bg-border">
                    {employees
                      ?.filter((emp) => selectedEmployees?.includes(emp?.id))
                      ?.map((employee) => {
                        const slotData = availabilityData?.[employee?.id]?.[
                          `0-${time}`
                        ] || { status: 'available' };
                        const statusIcon = getStatusIcon(slotData?.status);

                        return (
                          <button
                            key={employee?.id}
                            onClick={() =>
                              handleTimeSlotClick(
                                employee?.id,
                                0,
                                time,
                                slotData?.status
                              )
                            }
                            className={`
                            p-3 h-12 border transition-colors duration-150 flex items-center justify-center
                            ${getStatusColor(slotData?.status)}
                          `}>
                            {statusIcon && <Icon name={statusIcon} size={16} />}
                          </button>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (viewMode === 'week') {
    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            برنامه هفتگی
          </h3>
          <p className="text-sm text-muted-foreground">هفته ۱۵-۲۱ مهر ۱۴۰۳</p>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Days header */}
            <div className="grid grid-cols-8 border-b border-border">
              <div className="p-3 bg-muted border-l border-border">
                <span className="text-sm font-medium text-foreground">
                  زمان
                </span>
              </div>
              {persianDays?.map((day, index) => (
                <div
                  key={day}
                  className="p-3 bg-muted border-l border-border last:border-l-0 text-center">
                  <span className="text-sm font-medium text-foreground">
                    {day}
                  </span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {15 + index} مهر
                  </p>
                </div>
              ))}
            </div>

            {/* Time slots */}
            <div className="max-h-96 overflow-y-auto">
              {timeSlots?.slice(0, 12)?.map((time) => (
                <div
                  key={time}
                  className="grid grid-cols-8 border-b border-border last:border-b-0">
                  <div className="p-2 bg-muted border-l border-border flex items-center">
                    <span className="text-xs font-medium text-foreground">
                      {time}
                    </span>
                  </div>
                  {persianDays?.map((day, dayIndex) => {
                    // Show combined status for all selected employees
                    const hasAvailable = selectedEmployees?.some(
                      (empId) =>
                        availabilityData?.[empId]?.[`${dayIndex}-${time}`]
                          ?.status === 'available'
                    );
                    const hasBusy = selectedEmployees?.some(
                      (empId) =>
                        availabilityData?.[empId]?.[`${dayIndex}-${time}`]
                          ?.status === 'busy'
                    );

                    let cellStatus = 'available';
                    if (hasBusy && hasAvailable) cellStatus = 'mixed';
                    else if (hasBusy) cellStatus = 'busy';
                    else if (!hasAvailable) cellStatus = 'unavailable';

                    return (
                      <button
                        key={dayIndex}
                        onClick={() =>
                          handleTimeSlotClick(null, dayIndex, time, cellStatus)
                        }
                        className={`
                          p-2 h-10 border-l border-border last:border-l-0 transition-colors duration-150 flex items-center justify-center
                          ${
                            cellStatus === 'mixed'
                              ? 'bg-warning/20 border-warning/40 hover:bg-warning/30'
                              : getStatusColor(cellStatus)
                          }
                        `}>
                        {cellStatus === 'mixed' && (
                          <Icon name="AlertTriangle" size={12} />
                        )}
                        {cellStatus === 'available' && (
                          <Icon name="Check" size={12} />
                        )}
                        {cellStatus === 'busy' && <Icon name="X" size={12} />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Month view
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">برنامه ماهانه</h3>
        <p className="text-sm text-muted-foreground">مهر ۱۴۰۳</p>
      </div>
      {/* Days of week header */}
      <div className="grid grid-cols-7 border-b border-border">
        {persianDays?.map((day) => (
          <div
            key={day}
            className="p-3 bg-muted border-l border-border last:border-l-0 text-center">
            <span className="text-sm font-medium text-foreground">{day}</span>
          </div>
        ))}
      </div>
      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-px bg-border">
        {Array.from({ length: 35 }, (_, index) => {
          const dayNumber = index - 5; // Start from day -5 to show previous month days
          const isCurrentMonth = dayNumber > 0 && dayNumber <= 30;
          const isToday = dayNumber === 15;

          // Calculate availability summary for the day
          const dayAvailability = selectedEmployees?.reduce(
            (acc, empId) => {
              const morningSlot =
                availabilityData?.[empId]?.[`${index % 7}-۰۹:۰۰`];
              const afternoonSlot =
                availabilityData?.[empId]?.[`${index % 7}-۱۴:۰۰`];

              if (
                morningSlot?.status === 'available' ||
                afternoonSlot?.status === 'available'
              ) {
                acc.available++;
              }
              if (
                morningSlot?.status === 'busy' ||
                afternoonSlot?.status === 'busy'
              ) {
                acc.busy++;
              }
              return acc;
            },
            { available: 0, busy: 0 }
          );

          return (
            <button
              key={index}
              onClick={() =>
                isCurrentMonth &&
                handleTimeSlotClick(null, index % 7, 'day', 'available')
              }
              className={`
                h-24 p-2 bg-card hover:bg-muted transition-colors duration-150 text-right
                ${!isCurrentMonth ? 'opacity-40' : ''}
                ${isToday ? 'ring-2 ring-primary' : ''}
              `}
              disabled={!isCurrentMonth}>
              <div className="h-full flex flex-col">
                <span
                  className={`text-sm font-medium mb-1 ${
                    isToday ? 'text-primary' : 'text-foreground'
                  }`}>
                  {isCurrentMonth ? dayNumber : ''}
                </span>

                {isCurrentMonth && selectedEmployees?.length > 0 && (
                  <div className="flex-1 flex flex-col gap-1">
                    {dayAvailability?.available > 0 && (
                      <div className="text-xs bg-success/20 text-success px-1 py-0.5 rounded">
                        {dayAvailability?.available} آزاد
                      </div>
                    )}
                    {dayAvailability?.busy > 0 && (
                      <div className="text-xs bg-error/20 text-error px-1 py-0.5 rounded">
                        {dayAvailability?.busy} رزرو
                      </div>
                    )}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarGrid;
